import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import QuotationModal from './QuotationModal';
import Logo from './Logo';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path) => location.pathname === path;

  const NavLink = ({ to, children }) => (
    <Link 
      to={to} 
      className={`font-medium transition-all duration-300 relative group ${
        isActive(to) ? 'text-blue-700' : 'text-gray-700 hover:text-blue-700'
      }`}
      onClick={() => setIsMobileMenuOpen(false)}
    >
      {children}
      <span className={`absolute bottom-0 left-0 h-0.5 bg-blue-600 transition-all duration-300 ${isActive(to) ? 'w-full' : 'w-0 group-hover:w-full'}`}></span>
    </Link>
  );

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-lg py-3' : 'bg-transparent py-5'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group">
            <Logo className="h-10 w-auto md:h-12" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            <NavLink to="/about">About Us</NavLink>
            <NavLink to="/services">Services</NavLink>
            <NavLink to="/portfolio">Portfolio</NavLink>
            {/* <NavLink to="/industries">Industries</NavLink> */}
            <NavLink to="/contact">Contact Us</NavLink>
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <QuotationModal>
              <Button 
                className="bg-gradient-to-r from-blue-700 to-indigo-900 hover:from-blue-800 hover:to-indigo-950 text-white px-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5"
              >
                Get a Quote
              </Button>
            </QuotationModal>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-gray-700 hover:text-blue-900 transition-colors p-2"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden overflow-hidden bg-white/95 backdrop-blur-lg rounded-b-2xl shadow-xl border-t border-gray-100 absolute left-0 right-0 top-full"
            >
              <nav className="flex flex-col space-y-4 p-6">
                <Link to="/about" className="text-lg font-medium text-gray-700" onClick={() => setIsMobileMenuOpen(false)}>About Us</Link>
                <Link to="/services" className="text-lg font-medium text-gray-700" onClick={() => setIsMobileMenuOpen(false)}>Services</Link>
                <Link to="/portfolio" className="text-lg font-medium text-gray-700" onClick={() => setIsMobileMenuOpen(false)}>Portfolio</Link>
                {/* <Link to="/industries" className="text-lg font-medium text-gray-700" onClick={() => setIsMobileMenuOpen(false)}>Industries</Link> */}
                <Link to="/contact" className="text-lg font-medium text-gray-700" onClick={() => setIsMobileMenuOpen(false)}>Contact Us</Link>
                <div className="pt-2">
                  <QuotationModal>
                    <Button 
                      className="bg-gradient-to-r from-blue-700 to-indigo-900 hover:from-blue-800 hover:to-indigo-950 text-white w-full h-12 text-lg shadow-lg"
                    >
                      Get a Quote
                    </Button>
                  </QuotationModal>
                </div>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};

export default Header;
